import type Map from 'ol/Map';
import { getPointResolution } from 'ol/proj';

/** Reference laptop layout width — UI scales up above this on large / projected displays. */
const DESIGN_WIDTH_PX = 1280;
const MIN_UI_SCALE = 1;
const MAX_UI_SCALE = 1.75;
const MAX_MAP_ZOOM_OFFSET = 2.5;

export type DisplayScaleSnapshot = {
  uiScale: number;
  mapZoomOffset: number;
  isPresentationDisplay: boolean;
};

const CSS_UI_SCALE_VAR = '--digisaka-ui-scale';

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Derive UI + map zoom compensation from viewport size and pixel density.
 * Large external monitors and projectors get a higher scale so text and controls stay readable.
 */
export function computeDisplayScale(
  width = typeof window !== 'undefined' ? window.innerWidth : DESIGN_WIDTH_PX,
  height = typeof window !== 'undefined' ? window.innerHeight : 800,
  dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
): DisplayScaleSnapshot {
  const maxDim = Math.max(width, height);
  const widthRatio = width / DESIGN_WIDTH_PX;
  const isPresentationDisplay =
    width >= 1600 || (maxDim >= 1920 && widthRatio >= 1.12);

  let uiScale = MIN_UI_SCALE;

  if (isPresentationDisplay && width >= 1280) {
    uiScale = clamp(Math.sqrt(widthRatio), 1.08, MAX_UI_SCALE);
    // Mirrored / projector outputs often report a low device pixel ratio.
    if (dpr <= 1.25) {
      uiScale = clamp(uiScale * 1.12, MIN_UI_SCALE, MAX_UI_SCALE);
    }
    if (width >= 1920) {
      uiScale = clamp(uiScale * 1.05, MIN_UI_SCALE, MAX_UI_SCALE);
    }
  }

  const mapZoomOffset =
    uiScale > 1 ? clamp((uiScale - 1) * 3, 0, MAX_MAP_ZOOM_OFFSET) : 0;

  return { uiScale, mapZoomOffset, isPresentationDisplay };
}

export function applyDisplayScaleCss(snapshot: DisplayScaleSnapshot): void {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(
    CSS_UI_SCALE_VAR,
    snapshot.uiScale.toFixed(3),
  );
  document.documentElement.dataset.digisakaPresentation = snapshot.isPresentationDisplay
    ? 'true'
    : 'false';
}

export function getComputedDisplayScale(): number {
  if (typeof document === 'undefined') return 1;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(CSS_UI_SCALE_VAR);
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

/** Ground meters per screen pixel at the current map center (EPSG:3857 corrected). */
export function getGroundMetersPerPixel(map: Map): number {
  const view = map.getView();
  const resolution = view.getResolution();
  const center = view.getCenter();
  const projection = view.getProjection();
  if (resolution == null || !center || !projection) return NaN;
  return getPointResolution(projection, resolution, center);
}

/**
 * Inference ``resolution`` (m/px) derived from the live viewport extent and canvas size.
 * Keeps AI pipelines aligned with what the user actually sees on screen.
 */
export function deriveViewportResolutionMeters(map: Map, fallback = 1): number {
  const metersPerPixel = getGroundMetersPerPixel(map);
  if (!Number.isFinite(metersPerPixel) || metersPerPixel <= 0) {
    return fallback;
  }
  return clamp(Math.round(metersPerPixel * 100) / 100, 0.05, 15);
}

export function getMapLabelFontSizePx(basePx = 13): number {
  return Math.round(basePx * getComputedDisplayScale());
}

export function getMapPixelRatio(): number {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const uiScale = getComputedDisplayScale();
  const boosted = uiScale > 1.15 ? 1.1 : 1;
  return Math.min(Math.max(dpr, 1), 2) * boosted;
}

export function getInitialMapZoom(baseZoom = 10): number {
  const { mapZoomOffset } = computeDisplayScale();
  return Math.min(20, Math.round(baseZoom + mapZoomOffset));
}

export const DISPLAY_SCALE_CHANGE_EVENT = 'digisaka:display-scale-change';

export function dispatchDisplayScaleChange(snapshot: DisplayScaleSnapshot): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(DISPLAY_SCALE_CHANGE_EVENT, { detail: snapshot }),
  );
}
